import { Check, Copy } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCopy } from './useCopy';

const TOKEN = /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

/** Light syntax colouring for pretty-printed JSON, built as React nodes (no HTML injection). */
export function highlightJson(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let k = 0;
  for (const m of text.matchAll(TOKEN)) {
    const i = m.index ?? 0;
    if (i > last) out.push(text.slice(last, i));
    if (m[1]) {
      out.push(
        <span key={k++} className={m[2] ? 'tok-key' : 'tok-str'}>
          {m[1]}
        </span>,
      );
      if (m[2]) out.push(m[2]);
    } else if (m[3]) {
      out.push(
        <span key={k++} className="tok-bool">
          {m[3]}
        </span>,
      );
    } else if (m[4]) {
      out.push(
        <span key={k++} className="tok-num">
          {m[4]}
        </span>,
      );
    }
    last = i + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function CodeBlock({ value, json, label = 'code' }: { value: unknown; json?: boolean; label?: string }) {
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  const { copied, copy } = useCopy();
  return (
    <div className="ui-codeblock">
      <pre tabIndex={0} aria-label={label}>
        {json || typeof value !== 'string' ? highlightJson(text) : text}
      </pre>
      <button
        type="button"
        className="ui-copy ui-codeblock__copy"
        data-copied={copied}
        onClick={() => void copy(text)}
        aria-label={copied ? `Copied ${label}` : `Copy ${label}`}
        title={copied ? 'Copied' : 'Copy'}
      >
        {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
      </button>
    </div>
  );
}
